import { useCallback, useEffect, useRef } from 'react';
import { RouletteType } from '../../enums';
import { UseRouletteProps } from './props';

/**
 * Custom hook to manage a roulette spinning animation.
 * This hook provides functionalities to handle the spinning and standby states of a roulette.
 *
 * @param {UseRouletteProps} props - The properties to configure the roulette behavior.
 * @param {number} props.prizeIndex - The index of the prize to stop at.
 * @param {number} props.spinningTime - The duration of the spinning animation in seconds.
 * @param {boolean} props.start - Flag to indicate if the roulette should start spinning.
 * @param {Function} [props.callback] - Optional callback function to be called after spinning ends.
 * @param {number} [props.firstItem=5] - The index of the first item to display in standby state.
 * @param {RouletteType} props.type - The type of roulette, affecting transition behavior.
 * @returns {Object} An object containing a ref to the roulette container.
 *
 * @example
 * // Usage example:
 * const { containerRef } = useRoulette({
 *   prizeIndex: 3,
 *   spinningTime: 2,
 *   start: true,
 *   callback: () => function('Spinning ended'),
 *   firstItem: 0,
 *   type: RouletteType.Standard,
 * });
 */
export function useRoulette({
  prizeIndex,
  spinningTime,
  start,
  callback,
  firstItem = 5,
  type,
}: UseRouletteProps) {
  // Ref to the roulette container element
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Padding between items in the roulette
  const paddingInline = 8;

  /**
   * Calculates the new position for the roulette based on the given position and children elements.
   *
   * @param {number} position - The target position index.
   * @param {HTMLDivElement[]} children - The list of child elements in the container.
   * @returns {number} The calculated new position in pixels.
   */
  const calculateNewPosition = useCallback(
    (position: number, children: HTMLDivElement[]): number => {
      if (!children.length || !containerRef.current) return 0;

      const childWidth = children[0].clientWidth + paddingInline;
      const containerWidth = containerRef.current.clientWidth;

      return -(position * childWidth) + containerWidth / 2 - childWidth / 2;
    },
    [paddingInline],
  );

  /**
   * Handles the standby state of the roulette, positioning it to the first item.
   */
  const handleStandByState = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    const children = Array.from(
      containerRef.current.children,
    ) as HTMLDivElement[];
    const newPosition = calculateNewPosition(firstItem, children);

    containerRef.current.style.left = `${newPosition}px`;
    containerRef.current.style.transition =
      type == RouletteType.Infinity ? '0s' : '1s ease-in';
  }, [firstItem, calculateNewPosition, type]);

  /**
   * Handles the start of the spinning animation, moving the roulette to the prize index.
   */
  const handleStartSpinning = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    const container = containerRef.current;
    const children = Array.from(container.children) as HTMLDivElement[];
    const newPosition = calculateNewPosition(prizeIndex, children);

    container.style.left = `${newPosition}px`;
    container.style.transition = `left ${spinningTime}s ease-out`;

    const onTransitionEnd = () => {
      callback?.();
      container.removeEventListener('transitionend', onTransitionEnd);
    };

    container.addEventListener('transitionend', onTransitionEnd);
  }, [prizeIndex, spinningTime, callback, calculateNewPosition]);

  /**
   * Determines the current state of the roulette and triggers the appropriate action.
   */
  function handleRouletteState() {
    if (start) {
      handleStartSpinning();
    } else {
      handleStandByState();
    }
  }

  // Effect to handle the roulette state changes based on the start flag
  useEffect(handleRouletteState, [
    start,
    handleStartSpinning,
    handleStandByState,
  ]);

  return {
    containerRef,
  };
}

export type { UseRouletteProps };
